import React, { useMemo, useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import EmployeeRow from './EmployeeRow';

function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field;
  return (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide cursor-pointer select-none group"
      onClick={() => onSort(field)}
    >
      <div className="inline-flex items-center gap-1">
        {label}
        <span className={`transition-colors ${active ? 'text-orange-500' : 'text-gray-300 group-hover:text-gray-400'}`}>
          {active && sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </span>
      </div>
    </th>
  );
}

function EmployeeTable({ employees, onEdit, onToggleActive, onInvite }) {
  const [sortField, setSortField] = useState('fullName');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sorted = useMemo(
    () =>
      [...employees].sort((a, b) => {
        const av = a[sortField] ?? '';
        const bv = b[sortField] ?? '';
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return sortDir === 'asc' ? cmp : -cmp;
      }),
    [employees, sortField, sortDir]
  );

  const sortProps = { sortField, sortDir, onSort: handleSort };

  return (
    <div className="mx-6 mb-6 rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <SortHeader label="Name" field="fullName" {...sortProps} />
              <SortHeader label="Code" field="employeeCode" {...sortProps} />
              <SortHeader label="Role" field="jobRole" {...sortProps} />
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Phone
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Language
              </th>
              <SortHeader label="Hourly rate" field="hourlyRateCost" {...sortProps} />
              <SortHeader label="Default shift" field="defaultShiftTemplateLabel" {...sortProps} />
              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                WhatsApp
              </th>
              <SortHeader label="Status" field="isActive" {...sortProps} />
              <th className="px-4 py-3 w-12" aria-label="Actions" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {sorted.map(emp => (
              <EmployeeRow
                key={emp.id}
                employee={emp}
                onEdit={() => onEdit(emp)}
                onToggleActive={() => onToggleActive(emp)}
                onInvite={onInvite}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default React.memo(EmployeeTable);